import React from 'react';
import { Link } from 'react-router-dom';
import Header from './shared/Header';
import Footer from './shared/Footer';
import {
  Bot,
  Server,
  Layers,
  Code2,
  Rocket,
  ArrowRight,
  Database,
} from 'lucide-react';

const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const networkCapabilities = [
  {
    icon: <Bot size={22} />,
    title: 'AI & ML Engineering',
    description:
      'Engineers who have shipped production AI systems: LLM integrations, fine-tuning, evals, and inference optimization.',
  },
  {
    icon: <Server size={22} />,
    title: 'Infrastructure Architecture',
    description:
      'Architects who design for scale, reliability, and cost: cloud infrastructure, data platforms, and distributed systems.',
  },
  {
    icon: <Layers size={22} />,
    title: 'Product Design for AI',
    description:
      'Designers who understand AI constraints and build interfaces that make intelligent systems feel intuitive to end users.',
  },
  {
    icon: <Rocket size={22} />,
    title: 'Implementation & Delivery',
    description:
      'Delivery leads who keep engagements on track: scoping, sequencing, and surfacing risk before it becomes a problem.',
  },
  {
    icon: <Code2 size={22} />,
    title: 'Full-Stack Engineering',
    description:
      'Engineers who own the full surface: frontend, backend, APIs, and integrations, without handing off at every layer.',
  },
  {
    icon: <Database size={22} />,
    title: 'Data Engineering',
    description:
      'Specialists in pipelines, warehouses, and data modeling who make sure the right data gets to the right place reliably.',
  },
];

const TeamPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page header */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-blue-50 via-white to-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-6">
            Who we are
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            A boutique core. A senior network.
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            We operate as a small core team backed by a curated network of senior practitioners
            we assemble based on what your engagement needs.
          </p>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">

        {/* Section 1: Core Team */}
        <div className="mb-20 md:mb-28">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
            Core Team
          </h2>
          <p className="text-gray-500 text-sm mb-10">
            Leadership, direction, and engagement ownership.
          </p>
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
              <div className="flex items-start gap-6 mb-6">
                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-md flex-shrink-0">
                  <img
                    src="/SD.jpg"
                    alt="S.D. Hines"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gray-900">S.D. Hines</h3>
                  <p className="text-blue-600 font-medium mb-3">Managing Partner</p>
                  <a
                    href="https://linkedin.com/in/sarittahines"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-gray-400 hover:text-blue-600 transition-colors text-sm"
                  >
                    <LinkedInIcon />
                    LinkedIn
                  </a>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">
                S.D. brings a decade of experience in data engineering and cloud solutions from her
                time at Google, where she worked as a sales engineer helping enterprise teams unlock
                the value of Google Cloud Platform. She specializes in translating complex data
                problems into working systems, from architecture decisions through to delivery.
                DataBackfill, the studio's GA4-to-BigQuery product, has served hundreds of
                properties and millions of events since launch.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  'Google Cloud',
                  'Data Engineering',
                  'ETL Architecture',
                  'BigQuery',
                  'Python',
                  'React',
                  'Solution Design',
                ].map((skill) => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Our Network */}
        <div className="mb-20 md:mb-28">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
            Our Network
          </h2>
          <p className="text-gray-600 max-w-2xl mb-10 leading-relaxed">
            A curated network of senior engineers, architects, designers, and delivery leads
            we assemble based on engagement needs.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {networkCapabilities.map((cap) => (
              <div
                key={cap.title}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-blue-100
                           hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4 text-blue-600">
                  {cap.icon}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{cap.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{cap.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: How we assemble teams */}
        <div className="mb-12">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">
            How we assemble teams
          </h2>
          <div className="bg-blue-50 rounded-2xl p-8 md:p-12 max-w-3xl">
            <p className="text-gray-700 text-base md:text-lg leading-relaxed">
              Every engagement gets a small, senior team matched to the scope. We don't staff by
              availability. We staff by fit. That means you get operators who've shipped this kind
              of work before, not junior consultants learning on your dime.
            </p>
          </div>
        </div>

      </div>

      {/* Bottom CTA */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Want to work with us?</h2>
          <Link
            to="/contact"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700
                       text-white rounded-xl font-medium transition-all shadow-md hover:shadow-lg"
          >
            Start a conversation
            <ArrowRight className="ml-2" size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamPage;
